import type { FC, PropsWithChildren } from 'react'
import { css } from '@emotion/react'
import styled from '@emotion/styled'

import Typography from '@segment/matcha/components/Typography'

import CategoryHeaderBase, * as CategoryHeader from '@segment/matcha/recipes/CategoryHeader'
import CardsGrid, { CONFIG } from '@segment/matcha/recipes/CardsGrid'

import type { IntegrationGroup, FetchedCatalogIntegration } from '@segment/api/routes/catalog/queries'

import type { Hit } from '@segment/api/algolia/client'

import CatalogGridBase, * as CatalogGrid from '../CatalogGrid'
import IntegrationCardTemplate from '../../../components/IntegrationCard'

interface GroupedGridProps {
  onCategoryChange: (slug: string) => void
  destination: boolean
  source: boolean
  nextPageTrigger: JSX.Element
  flattenedPages?: IntegrationGroup<Hit<FetchedCatalogIntegration>>[]
  isEmpty?: boolean
  isLoading?: boolean
}

const GroupedGrid: FC<PropsWithChildren<GroupedGridProps>> = ({
  onCategoryChange,
  nextPageTrigger,
  flattenedPages,
  isEmpty,
  destination,
  source,
  isLoading,
}) => {
  return (
    <CatalogGridBase aria-live="polite" aria-busy={isLoading}>
      {flattenedPages?.map(({ category, integrations }, index) => (
        <CategoryGroup
          key={index}
          category={category}
          integrations={integrations}
          onCategoryChange={onCategoryChange}
        />
      ))}
      {isEmpty && !destination && !source && (
        <EmptyView>
          <NoMarginTypography variant="h4">Sorry, we couldn’t find anything that fit your search.</NoMarginTypography>
          <NoMarginTypography variant="pMedium">Please select at least one integration type.</NoMarginTypography>
        </EmptyView>
      )}
      {nextPageTrigger}
    </CatalogGridBase>
  )
}

export default GroupedGrid

interface CategoryGroupProps {
  category?: {
    name: string
    slug: string
  }
  integrations: Array<Hit<FetchedCatalogIntegration>>
  onCategoryChange: (slug: string) => void
}

const CategoryGroup: FC<PropsWithChildren<CategoryGroupProps>> = ({ category, onCategoryChange, integrations }) => (
  <CatalogGrid.Group>
    {category && (
      <CategoryHeaderBase>
        <CategoryHeader.Title variant="h4">{category.name}</CategoryHeader.Title>
        <ShowMoreButton integrationsLength={integrations.length} onClick={() => onCategoryChange(category.slug)}>
          View more
        </ShowMoreButton>
      </CategoryHeaderBase>
    )}
    <CardsGrid limitItems={true}>
      {integrations.map((integration) => (
        <IntegrationCardTemplate key={integration.objectID} integration={integration} />
      ))}
    </CardsGrid>
  </CatalogGrid.Group>
)

interface ShowMoreButtonProps {
  integrationsLength: number
}
const ShowMoreButton = styled(CategoryHeader.Button)<ShowMoreButtonProps>`
  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    ${({ integrationsLength }) =>
      integrationsLength < CONFIG.itemsLimit.mobile &&
      css`
        display: none;
      `}
  }

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    ${({ integrationsLength }) =>
      integrationsLength < CONFIG.itemsLimit.desktop &&
      css`
        display: none;
      `}
  }
`

const NoMarginTypography = styled(Typography)`
  margin: 0;
`

const EmptyView = styled.div`
  display: grid;
  gap: 48px;
`
